import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Spinner, Alert, Row, Col } from "reactstrap";
import CourseSidebar from "./Component/Sidebar";
import CourseService from "../../services/CourseService";
import Quiz from "../Quiz/Quiz";
import "./css/courseDetail.css";
import { MoveLeft, MoveRight } from "lucide-react";
import Arrow from "./Component/Arrow";
import arrowIcon from "../../assets/kangaroo/Icon 1.png"
import LeftArrow from "../../assets/kangaroo/Icon 2.png"
import RightArrow from "../../assets/kangaroo/Icon 3.png"

const flattenNodes = (children = []) => {
  const nodes = [];

  const sortByOrder = (arr = []) =>
    [...arr].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  const processSubDetails = (subDetails = [], parentChild) => {
    sortByOrder(subDetails).forEach((sub) => {
      nodes.push({ type: "subDetail", data: sub, parentChild });

      // Always add textFields regardless of hasSubMenu
      if (sub.textFields?.length > 0) {
        sub.textFields.forEach((tf) => {
          nodes.push({
            type: "textField",
            data: tf,
            parentSub: sub,
            parentChild,
          });
        });
      }

      // Recurse into nested childrenSubDetails
      if (sub.childrenSubDetails?.length > 0) {
        processSubDetails(sub.childrenSubDetails, sub);
      }

      // Quiz for this subDetail
      if (sub.isQuiz && sub.quizSlug) {
        nodes.push({
          type: "quiz",
          data: { ...sub, id: `quiz-${sub.id}` },
          quizSlug: sub.quizSlug,
          parentChild,
        });
      }
    });
  };

  sortByOrder(children).forEach((child) => {
    nodes.push({
      type: child.isMainChild ? "child" : "standalone",
      data: child,
    });

    // Always add child-level textFields regardless of hasSubMenu
    if (child.textFields?.length > 0) {
      child.textFields.forEach((tf) => {
        nodes.push({
          type: "textField",
          data: tf,
          parentSub: null,
          parentChild: child,
        });
      });
    }

    if (child.childrenSubDetails?.length > 0) {
      processSubDetails(child.childrenSubDetails, child);
    }

    // Quiz for this child
    if (child.isQuiz && child.quizSlug) {
      nodes.push({
        type: "quiz",
        data: { ...child, id: `quiz-${child.id}` },
        quizSlug: child.quizSlug,
        parentChild: child,
      });
    }
  });

  return nodes;
};





const resolveContent = (selection) => {
  if (!selection?.data) return { heading: "", description: "" };
  return {
    heading: selection.data.heading || "Section",
    description: selection.data.description || "",
  };
};


const CourseDetail = () => {
  const { id, contentId } = useParams();
  const navigate = useNavigate()
  const containerRef = useRef(null);



  const {
    data: course,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["courseDetail", id],
    queryFn: () => CourseService.getDetails(id),
    enabled: !!id,
  });


  const [selected, setSelected] = useState(null);

  // Auto-select first meaningful non-quiz content on load
  useEffect(() => {
    if (!course) return;

    const nodes = flattenNodes(course.children || []);

    if (nodes.length === 0) return;

    // 1️⃣ If URL has contentId → restore it
    if (contentId) {
      const matched = nodes.find(
        (n) => n.data?.id === contentId
      );

      if (matched) {
        setSelected(matched);
        return;
      }
    }

    // 2️⃣ If no contentId or invalid contentId → select first node
    console.log("fired")
    const firstNode = nodes[0];
    setSelected(firstNode);

    navigate(`/course/${id}/${firstNode.data?.id}`, {
      replace: true,
    });

  }, [course, contentId, id, navigate]);

  const allNodes = course ? flattenNodes(course.children || []) : [];

  // Match selected node by its data.id (quiz nodes use `quiz-{id}` as their id)
  const currentIndex = allNodes.findIndex(
    (n) => n.data?.id === selected?.data?.id,
  );
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < allNodes.length - 1;

  const { heading, description } = resolveContent(selected);

  // Only show quiz when type === "quiz" and quizSlug is present
  const isQuizView = selected?.type === "quiz" && !!selected?.quizSlug;

  const processHtml = (html = "") => {
    if (!html) return "";
    let processed = html;

    processed = processed.replace(
      /<oembed[^>]+url="([^"]+)"[^>]*><\/oembed>/gi,
      (_, url) => convertToIframe(url),
    );

    processed = processed.replace(
      /<a[^>]+href="([^"]+)"[^>]*>(.*?)<\/a>/gi,
      (_, url, text) => {
        if (url.includes("youtube") || url.includes("youtu.be")) {
          return convertToIframe(url);
        }
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      },
    );

    return processed;
  };

  const convertToIframe = (url) => {
    let videoId = null;
    const match =
      url.match(/youtu\.be\/([^?&]+)/) ||
      url.match(/youtube\.com\/watch\?v=([^&]+)/) ||
      url.match(/youtube\.com\/embed\/([^?&]+)/) ||
      url.match(/youtube\.com\/shorts\/([^?&]+)/);

    if (match) videoId = match[1];

    if (videoId) {
      return `
        <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; border-radius: 10px;">
          <iframe
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
            src="https://www.youtube.com/embed/${videoId}"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      `;
    }

    return `<iframe src="${url}" style="width:100%; height:500px; border:none; border-radius:10px;" allowfullscreen></iframe>`;
  };

  const processedDescription = processHtml(description);
  const formattedDescription = processedDescription
    .replace(/&lt;&lt;break&gt;&gt;/g, "<br/>")
    .replace(/<<break>>/g, "<br/>")

    // 50_width (your existing logic)
    .replace(
      /<p>\s*class=50_width\s*<\/p>/gi,
      '<span class="is-50-width" style="display:none;"></span>'
    )
    .replace(
      /class=50_width/gi,
      '<span class="is-50-width" style="display:none;"></span>'
    )

    // 🔥 facts (Marker pattern)
    .replace(
      /<p>\s*class=facts\s*<\/p>/gi,
      '<span class="is-facts" style="display:none;"></span>'
    )
    .replace(
      /class=facts/gi,
      '<span class="is-facts" style="display:none;"></span>'
    )

  const cleanedDescription = formattedDescription.replace(
    /<(p|h1|h2|h3|h4|h5)>(?:&nbsp;|\s)*<\/\1>/g,
    '<$1 class="empty-line"></$1>'
  );

  // Handle sidebar selection — sidebar passes the raw node shape
  const handleSidebarSelect = (selection) => {
    let updatedSelection = selection;

    // Fix quiz ID format
    if (selection.type === "quiz") {
      const originalId = selection.data?.id;

      updatedSelection = {
        ...selection,
        data: {
          ...selection.data,
          id: originalId?.startsWith("quiz-")
            ? originalId
            : `quiz-${originalId}`,
        },
      };
    }

    navigate(`/course/${id}/${updatedSelection.data?.id}`);


  };
  useEffect(() => {
    console.log("called");

    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 50);
  }, [id, contentId, selected?.data?.id]);

  return (
    <div style={{ display: "flex", backgroundColor: "#ffffff" }}>
      {/* Sidebar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "calc(100vh - 95px)",
          overflowY: "auto",
          background: "#fff",
          borderRight: "1px solid #eee",
          flexShrink: 0,
        }}
      >
        <CourseSidebar
          activeId={selected?.data?.id}
          onSelectChild={handleSidebarSelect}
        />
      </div>

      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "calc(100vh - 100px)",

        }}
      >
        <div className=" flex-grow-1 overflow-auto " style={{ padding: "6rem", paddingLeft: "12rem", paddingRight: "12rem" }} ref={containerRef}>
          {isLoading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: "60vh" }}
            >
              <Spinner color="secondary" />
            </div>
          ) : isError ? (
            <Alert color="danger" className="mt-4">
              {error?.message || "Failed to load course content."}
            </Alert>
          ) : (
            <>
              <h4
                className={` mb-4 ${currentIndex === 0
                  ? "font-level-8"
                  : "font-level-2 text-muted"
                  }`}
              >
                {course?.name}
              </h4>
              <hr className="mb-5" style={{ color: "#878080ff" }} />

              {selected ? (
                isQuizView ? (
                  <div >
                    <h5
                      className="font-level-7 mb-4"
                      style={{ color: "#7B1FA2" }}
                    >
                      Check your Understanding:
                    </h5>
                    <div >

                      <Quiz quizSlug={selected.quizSlug} data={selected.data} id={id} courseName={course?.name} />
                    </div>
                  </div>
                ) : (
                  <div>
                    {processedDescription ? (
                      <div
                        className="description-wrapper"
                        dangerouslySetInnerHTML={{
                          __html: cleanedDescription,
                        }}
                      />
                    ) : (
                      <p className="text-muted mt-3">
                        No detailed content available for this section.
                      </p>
                    )}
                  </div>
                )
              ) : null}
            </>
          )}
          {!isLoading && !isError && allNodes.length > 0 && (
            <div
              style={{
                borderTop: "1px solid #ddd",
                padding: "16px 0px",
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "#FFFFFF",
                flexShrink: 0,
                marginTop: "3rem",
                paddingTop: "6rem",
                marginBottom: "12rem"
              }}
            >
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: hasPrev ? "#5a0b99" : "#ccc",
                  fontWeight: 600,
                  cursor: hasPrev ? "pointer" : "default",
                  textDecoration: hasPrev ? "none" : "none",
                }}
                onClick={() => {
                  if (hasPrev) {
                    setSelected(allNodes[currentIndex - 1]);
                    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                disabled={!hasPrev}
              >
                <div className="d-flex justify-content-start align-items-center ">


                  <img src={hasPrev ? RightArrow : arrowIcon} alt="" style={{ width: "15%", transform: hasPrev ? "rotate(180deg)" : "none" }} />


                  <p className="m-0 p-0 country-paragraph-text text-decoration-none ms-3" style={{ color: hasPrev ? "#86158E" : "#D6DCED" }}>
                    Previous
                  </p>


                </div>
              </button>

              <button
                style={{
                  background: "none",
                  border: "none",
                  color: hasNext ? "#5a0b99" : "#ccc",
                  fontWeight: 600,
                  cursor: hasNext ? "pointer" : "default",
                  textDecoration: hasNext ? "none" : "none",
                }}
                onClick={() => {
                  if (hasNext) {
                    setSelected(allNodes[currentIndex + 1]);
                    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                disabled={!hasNext}
              >
                <div className="d-flex justify-content-end align-items-center ">
                  <p className="m-0 p-0 country-paragraph-text text-decoration-none me-3" style={{ color: hasNext ? "#86158E" : "#D6DCED" }}>
                    Next
                  </p>
                  <img src={hasNext ? LeftArrow : arrowIcon} alt="" style={{ width: "15%", transform: hasNext ? "rotate(180deg)" : "rotate(180deg)" }} />

                </div>

              </button>
            </div>
          )}
        </div>

        {/* Prev / Next Footer */}

      </div>
    </div>
  );
};

export default CourseDetail;
